import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { RESOURCE_ORDER, type ResourceKey } from '../../lib/game';
import { ResourceCounter } from './ResourceCounter';

export interface ResourceBarEntry {
  resource: ResourceKey;
  amount: number;
  ratePerHour?: number;
  capacity?: number;
}

export interface ResourceBarProps {
  entries: ResourceBarEntry[];
  /** Left slot — logo, alliance tag, planet switcher. */
  leading?: ReactNode;
  /** Right slot — account level, notifications, avatar. */
  trailing?: ReactNode;
  /** Stick to the top of the viewport. */
  sticky?: boolean;
  /** Clicking a counter opens its statistics panel. */
  onOpenStats?: (resource: ResourceKey) => void;
  className?: string;
}

/**
 * The persistent top HUD strip (GDD §4 wireframe, row 1).
 *
 * Entries are sorted into the canonical resource order regardless of the order
 * they're passed in, so the bar never reshuffles between screens — muscle
 * memory for "where is my Titan" is worth protecting.
 *
 * Responsive behaviour: the strip scrolls horizontally on narrow screens rather
 * than wrapping to a second line, which would push the game view down on phones.
 */
export function ResourceBar({
  entries,
  leading,
  trailing,
  sticky = false,
  onOpenStats,
  className,
}: ResourceBarProps) {
  const ordered = [...entries].sort(
    (a, b) => RESOURCE_ORDER.indexOf(a.resource) - RESOURCE_ORDER.indexOf(b.resource),
  );

  return (
    <header
      data-tour="resource-bar"
      className={cn(
        'z-hud flex items-center gap-3 border-b border-edge bg-surface-1/95 px-3 py-2',
        'backdrop-blur supports-[backdrop-filter]:bg-surface-1/80',
        sticky && 'sticky top-0',
        className,
      )}
    >
      {leading ? <div className="flex shrink-0 items-center gap-2">{leading}</div> : null}

      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-1 overflow-x-auto',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {ordered.map((entry) => (
          <ResourceCounter
            key={entry.resource}
            resource={entry.resource}
            amount={entry.amount}
            ratePerHour={entry.ratePerHour}
            capacity={entry.capacity}
            onOpenStats={onOpenStats ? () => onOpenStats(entry.resource) : undefined}
          />
        ))}
      </div>

      {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
    </header>
  );
}
